import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { stylingHelpers } from '../../designTokens';

const { flexCenter } = stylingHelpers;

const baseStyles = ({ theme }) => css`
  border-radius: ${theme.borderRadius.circle};
  overflow: hidden;
  padding: 2px;
  position: relative;

  ${flexCenter()};

  &:before {
    content: '';
    border-radius: ${theme.borderRadius.circle};
    width: 100%;
    height: 100%;
  }
`;

const variantStyles = ({ theme, variant }) => {
  const variantMap = {
    default: {
      bgColor: theme.colors.n700,
      bgColorBefore: theme.colors.white,
    },
    solid: {
      bgColor: theme.colors.n500,
      bgColorBefore: theme.colors.n500,
    },
    whiteTransparent: {
      borderBefore: `2px solid ${theme.colors.white}`,
      bgColor: 'none',
      bgColorBefore: 'none',
    },
    grey: {
      bgColor: theme.colors.n700,
      bgColorBefore: '#f2f2f2',
    },
  };

  const { borderBefore, bgColor, bgColorBefore } = variantMap[variant] || variantMap.default;

  return css`
    background: ${bgColor};

    &:before {
      border: ${borderBefore || 'none'};
      background: ${bgColorBefore};
    }
  `;
};

const sizeStyles = ({ size }) => {
  const sizeMap = {
    bit: {
      width: '20px',
      height: '20px',
    },
    byte: {
      width: '28px',
      height: '28px',
    },
    giga: {
      width: '33px',
      height: '33px',
    },
    tera: {
      width: '37px',
      height: '37px',
    },
    peta: {
      width: '40px',
      height: '40px',
    },
    zetta: {
      width: '103px',
      height: '103px',
      padding: '4px',
    },
  };

  const sizeProps = sizeMap[size] || sizeMap.giga;

  return css`
    ${sizeProps};
  `;
};

const borderColorStyles = ({ theme, borderColor }) =>
  borderColor &&
  css`
    background: ${theme.colors[borderColor]};
  `;

const thinStyles = ({ thin }) =>
  thin &&
  css`
    padding: 1px;
  `;

const dashedStyles = ({ theme, dashed }) =>
  dashed &&
  css`
    border: 1px dashed ${theme.colors.n700};
    background: none;

    &:before {
      background: none;
    }
  `;

const Container = styled('div')(baseStyles, variantStyles, sizeStyles, borderColorStyles, thinStyles, dashedStyles);

const wrapperBaseStyles = ({ theme }) => css`
  color: ${theme.colors.white};
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  text-align: center;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const wrapperPaddingStyles = ({ theme, hasPadding }) =>
  hasPadding &&
  css`
    padding: ${theme.spacings.byte};
  `;

const Wrapper = styled('div')(wrapperBaseStyles, wrapperPaddingStyles);

const Frame = ({ children, hasPadding, ...props }) => (
  <Container {...props}>
    <Wrapper hasPadding={hasPadding}>{children}</Wrapper>
  </Container>
);

Frame.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  hasPadding: PropTypes.bool,
};

export default Frame;
